'use client';
import React from 'react';

// Deterministic 0–9 index from any string id
function patternIndex(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h) % 10;
}

// Safe uid for gradient/clip IDs
function uid(id: string): string {
  return id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10) || 'x';
}

// ── Pattern factories ─────────────────────────────────────────────────────────
// Lightweight, icon-forward thumbnails for news cards. Each pattern pairs a
// mid-saturated gradient background with a single white glyph so the image
// reads cleanly at 56×56px on both dark and light app backgrounds.

type GradientStop = { from: string; to: string };
type Palette = { bg: GradientStop; glyph: string; soft: string };

const PALETTES: Palette[] = [
  { bg: { from: '#059669', to: '#047857' }, glyph: '#ecfdf5', soft: '#a7f3d0' }, // 0 emerald
  { bg: { from: '#2563eb', to: '#1d4ed8' }, glyph: '#eff6ff', soft: '#bfdbfe' }, // 1 blue
  { bg: { from: '#e11d48', to: '#be123c' }, glyph: '#fff1f2', soft: '#fecdd3' }, // 2 rose
  { bg: { from: '#7c3aed', to: '#6d28d9' }, glyph: '#f5f3ff', soft: '#ddd6fe' }, // 3 violet
  { bg: { from: '#d97706', to: '#b45309' }, glyph: '#fffbeb', soft: '#fde68a' }, // 4 amber
  { bg: { from: '#0891b2', to: '#0e7490' }, glyph: '#ecfeff', soft: '#a5f3fc' }, // 5 cyan
  { bg: { from: '#0d9488', to: '#0f766e' }, glyph: '#f0fdfa', soft: '#99f6e4' }, // 6 teal
  { bg: { from: '#4f46e5', to: '#4338ca' }, glyph: '#eef2ff', soft: '#c7d2fe' }, // 7 indigo
  { bg: { from: '#db2777', to: '#be185d' }, glyph: '#fdf2f8', soft: '#fbcfe8' }, // 8 pink
  { bg: { from: '#475569', to: '#334155' }, glyph: '#f8fafc', soft: '#cbd5e1' }, // 9 slate
];

function Frame({ id, slot, children }: { id: string; slot: number; children: (glyph: string, soft: string) => React.ReactNode }) {
  const pal = PALETTES[slot];
  const g = `fp${slot}-${uid(id)}`;
  return (
    <svg viewBox="0 0 200 200" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={pal.bg.from} />
          <stop offset="100%" stopColor={pal.bg.to} />
        </linearGradient>
      </defs>
      <rect width="200" height="200" fill={`url(#${g})`} />
      {children(pal.glyph, pal.soft)}
    </svg>
  );
}

const PATTERNS: ((id: string) => React.ReactElement)[] = [

  // 0 ─ Rising line chart
  (id) => (
    <Frame key={id} id={id} slot={0}>
      {(glyph, soft) => (
        <>
          <polyline points="30,150 70,130 100,110 130,85 170,55" fill="none" stroke={soft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
          <polyline points="30,150 70,130 100,110 130,85 170,55" fill="none" stroke={glyph} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="170" cy="55" r="6" fill={glyph} />
        </>
      )}
    </Frame>
  ),

  // 1 ─ Bar chart
  (id) => (
    <Frame key={id} id={id} slot={1}>
      {(glyph) => (
        <g fill={glyph}>
          <rect x="40"  y="120" width="20" height="40" rx="2" opacity="0.6" />
          <rect x="72"  y="90"  width="20" height="70" rx="2" opacity="0.75" />
          <rect x="104" y="70"  width="20" height="90" rx="2" opacity="0.85" />
          <rect x="136" y="50"  width="20" height="110" rx="2" />
        </g>
      )}
    </Frame>
  ),

  // 2 ─ Declining line chart (bearish)
  (id) => (
    <Frame key={id} id={id} slot={2}>
      {(glyph, soft) => (
        <>
          <polyline points="30,55 70,75 100,100 130,120 170,150" fill="none" stroke={soft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
          <polyline points="30,55 70,75 100,100 130,120 170,150" fill="none" stroke={glyph} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="170" cy="150" r="6" fill={glyph} />
        </>
      )}
    </Frame>
  ),

  // 3 ─ Pie chart slice
  (id) => (
    <Frame key={id} id={id} slot={3}>
      {(glyph, soft) => (
        <g>
          <circle cx="100" cy="100" r="55" fill={soft} opacity="0.35" />
          <path d="M100,100 L100,45 A55,55 0 0 1 148,128 Z" fill={glyph} />
          <circle cx="100" cy="100" r="12" fill={soft} opacity="0.9" />
        </g>
      )}
    </Frame>
  ),

  // 4 ─ Dollar sign
  (id) => (
    <Frame key={id} id={id} slot={4}>
      {(glyph, soft) => (
        <g>
          <circle cx="100" cy="100" r="50" fill="none" stroke={soft} strokeWidth="3" opacity="0.5" />
          <text x="100" y="128" textAnchor="middle" fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" fontWeight="700" fontSize="72" fill={glyph}>$</text>
        </g>
      )}
    </Frame>
  ),

  // 5 ─ Radar / concentric rings
  (id) => (
    <Frame key={id} id={id} slot={5}>
      {(glyph, soft) => (
        <g transform="translate(100,100)">
          <circle r="55" fill="none" stroke={soft} strokeWidth="1.5" opacity="0.4" />
          <circle r="38" fill="none" stroke={soft} strokeWidth="1.5" opacity="0.55" />
          <circle r="22" fill="none" stroke={glyph} strokeWidth="2" opacity="0.8" />
          <circle r="8" fill={glyph} />
          <line x1="0" y1="0" x2="42" y2="-30" stroke={glyph} strokeWidth="3" strokeLinecap="round" />
        </g>
      )}
    </Frame>
  ),

  // 6 ─ Candlestick cluster
  (id) => (
    <Frame key={id} id={id} slot={6}>
      {(glyph, soft) => (
        <g>
          {/* wicks */}
          <line x1="60"  y1="50"  x2="60"  y2="150" stroke={soft} strokeWidth="2" opacity="0.6" />
          <line x1="100" y1="55"  x2="100" y2="150" stroke={soft} strokeWidth="2" opacity="0.6" />
          <line x1="140" y1="45"  x2="140" y2="155" stroke={soft} strokeWidth="2" opacity="0.6" />
          {/* bodies */}
          <rect x="50"  y="75"  width="20" height="55" fill={glyph} />
          <rect x="90"  y="100" width="20" height="35" fill={soft} opacity="0.85" />
          <rect x="130" y="60"  width="20" height="65" fill={glyph} />
        </g>
      )}
    </Frame>
  ),

  // 7 ─ Wave chart
  (id) => (
    <Frame key={id} id={id} slot={7}>
      {(glyph, soft) => (
        <>
          <path d="M20,140 C50,100 80,160 110,110 C140,70 170,130 190,95" fill="none" stroke={soft} strokeWidth="2" opacity="0.45" strokeLinecap="round" />
          <path d="M20,120 C50,80 80,140 110,90 C140,50 170,110 190,75" fill="none" stroke={glyph} strokeWidth="5" strokeLinecap="round" />
          <circle cx="190" cy="75" r="6" fill={glyph} />
        </>
      )}
    </Frame>
  ),

  // 8 ─ Arrow up-right
  (id) => (
    <Frame key={id} id={id} slot={8}>
      {(glyph, soft) => (
        <g>
          <circle cx="100" cy="100" r="55" fill={soft} opacity="0.25" />
          <path
            d="M70,130 L130,70 M130,70 L90,70 M130,70 L130,110"
            fill="none"
            stroke={glyph}
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      )}
    </Frame>
  ),

  // 9 ─ Globe / grid
  (id) => (
    <Frame key={id} id={id} slot={9}>
      {(glyph, soft) => (
        <g transform="translate(100,100)">
          <circle r="55" fill="none" stroke={glyph} strokeWidth="3" />
          <ellipse cx="0" cy="0" rx="55" ry="20" fill="none" stroke={soft} strokeWidth="2" opacity="0.8" />
          <ellipse cx="0" cy="0" rx="20" ry="55" fill="none" stroke={soft} strokeWidth="2" opacity="0.8" />
          <line x1="-55" y1="0" x2="55" y2="0" stroke={soft} strokeWidth="2" opacity="0.8" />
        </g>
      )}
    </Frame>
  ),
];

// ── Public components ─────────────────────────────────────────────────────────

interface FinancePatternProps {
  id: string;
  className?: string;
}

/** Renders one of 10 deterministic abstract patterns based on the id. */
export default function FinancePattern({ id, className }: FinancePatternProps) {
  return (
    <div className={`overflow-hidden ${className ?? ''}`} aria-hidden="true">
      {PATTERNS[patternIndex(id)](id)}
    </div>
  );
}

interface NewsImageProps {
  id: string;
  imageUrl?: string;
  className?: string;
  alt?: string;
}

/**
 * Shows a real image (from RSS) if available; falls back to an abstract pattern.
 */
export function NewsImage({ id, imageUrl, className, alt = '' }: NewsImageProps) {
  const [failed, setFailed] = React.useState(false);

  if (imageUrl && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageUrl}
        alt={alt}
        className={className}
        onError={() => setFailed(true)}
      />
    );
  }

  return <FinancePattern id={id} className={className} />;
}
